import {useEffect, useRef, useState} from "react";
import {useLocation} from "react-router-dom";
import {useMessages} from "i18n";
import {isHomeRoute} from "featurePanelRoutes";
import {useIsomorphicLayoutEffect} from "utils/useIsomorphicLayoutEffect";
import {useDownloadReply} from "download/reply/DownloadReplyProvider";
import {ComposerCanvas, REPLY_SLIDE_MS, ReplyButton, ReplyFlowColumn, ReplyHeader, ReplyPanel} from "download/reply/ReplyAction.styles";
import CreateFlow from "./CreateFlow";
import {CampaignSurface} from "./landing/CampaignSurface";

interface Props {
	covered?: boolean;
	homeVisible?: boolean;
}

export default function HomePage({covered = false, homeVisible = true}: Props) {
	const {sender, download} = useMessages();
	const {pathname} = useLocation();
	const reply = useDownloadReply();
	const column = useRef<HTMLDivElement>(null);
	const closeButton = useRef<HTMLButtonElement>(null);
	/*
	 * Closing plays the slide-in backwards (owner 2026-09-10). The provider flips `open` off at once,
	 * so this holds the panel's geometry mounted for the exit animation's length — otherwise the
	 * ComposerCanvas hides the tree the same frame and there is nothing left to animate. Reduced
	 * motion skips the hold: no exit animation, so no reason to keep the closed panel around.
	 */
	const [closing, setClosing] = useState(false);
	const wasOpen = useRef(false);
	useEffect(() => {
		if (reply.open) { wasOpen.current = true; setClosing(false); return; }
		if (!wasOpen.current) return;
		wasOpen.current = false;
		if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
		setClosing(true);
		const timer = window.setTimeout(() => setClosing(false), REPLY_SLIDE_MS);
		return () => window.clearTimeout(timer);
	}, [reply.open]);
	const replyShown = reply.open || closing;
	useIsomorphicLayoutEffect(() => {
		const anchor = reply.anchor;
		const surface = column.current;
		if (!reply.open || !anchor || !surface) return;
		const measure = () => {
			const rect = anchor.getBoundingClientRect();
			surface.style.setProperty("--reply-left", `${rect.left}px`);
			surface.style.setProperty("--reply-top", `${rect.top}px`);
			surface.style.setProperty("--reply-width", `${rect.width}px`);
			surface.style.setProperty("--reply-height", `${rect.height}px`);
		};
		measure();
		closeButton.current?.focus({preventScroll: true});
		const observer = new ResizeObserver(measure);
		observer.observe(anchor);
		window.addEventListener("resize", measure);
		window.addEventListener("scroll", measure, true);
		return () => { observer.disconnect(); window.removeEventListener("resize", measure); window.removeEventListener("scroll", measure, true); };
	}, [reply.open, reply.anchor]);
	const flowCovered = covered && !reply.open;
	return (
		<ComposerCanvas $home={homeVisible} $open={replyShown}>
			<CampaignSurface surface="landing" active={isHomeRoute(pathname) && !covered} composerOnly={!homeVisible}>
				<ReplyFlowColumn ref={column} $reply={replyShown} $closing={closing} data-drop-container="" data-drop-covered={flowCovered ? "" : undefined}
					role="region" aria-label={replyShown ? download.reply.pull : sender.dropRegion}>
					<ReplyPanel $reply={replyShown} data-testid={reply.open ? "reply-panel" : undefined}
						onKeyDown={event => {
							if (reply.open && event.key === "Escape" && !event.defaultPrevented && !document.querySelector('[role="dialog"]')) {
								event.stopPropagation(); reply.close();
							}
						}}>
						{replyShown && <ReplyHeader><h2>{download.reply.pull}</h2><ReplyButton ref={closeButton} aria-label={download.reply.close} onClick={reply.close}>×</ReplyButton></ReplyHeader>}
						<CreateFlow covered={flowCovered} replyTarget={reply.target} replyActive={replyShown}/>
					</ReplyPanel>
				</ReplyFlowColumn>
			</CampaignSurface>
		</ComposerCanvas>
	);
}
